import {
  joinListenAddress,
  splitListenAddress,
  validateListenHost,
} from "./plc-protocol-servers-model";

let nextMappingKey = 1;

const integer = (value) => {
  const text = String(value ?? "").trim();
  return text === "" ? Number.NaN : Number(text);
};

const requireInteger = (value, label, min, max) => {
  const parsed = integer(value);
  return Number.isInteger(parsed) && parsed >= min && parsed <= max
    ? ""
    : `${label} must be a whole number from ${min} to ${max}.`;
};

const appendError = (current, next) => (current ? `${current} ${next}` : next);

const hasForbiddenNodeIdCharacter = (value) => {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint < 32 || codePoint === 127 || character === ";" || character === "=") {
      return true;
    }
  }
  return false;
};

export function createOpcuaServerMapping(overrides = {}) {
  return {
    key: overrides.key || `opcua-server-map-${nextMappingKey++}`,
    nodeId: String(overrides.nodeId ?? overrides.node_id ?? ""),
    tagPath: String(overrides.tagPath ?? overrides.tag_path ?? ""),
    access: overrides.access === "read_write" ? "read_write" : "read",
    dataType: String(overrides.dataType ?? overrides.data_type ?? ""),
  };
}

export function opcuaServerFormFromConfig(config = {}) {
  const listener = splitListenAddress(config.listen_address || "127.0.0.1:4840");
  return {
    revision: Number(config.revision || 0),
    digest: String(config.digest || ""),
    enabled: false,
    listenHost: listener.host,
    listenPort: listener.port,
    namespaceUri: String(config.namespace_uri || "urn:controlseat:tags"),
    sourceCidrsText: (Array.isArray(config.source_cidrs) ? config.source_cidrs : [])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join("\n"),
    securityMode: "sign_and_encrypt",
    securityPolicy: "aes256_sha256_rsapss",
    anonymousEnabled: false,
    identityMode: config.identity_mode === "certificate_only"
      ? "certificate_only"
      : "username_or_certificate",
    identityProvider: String(config.identity_provider || "controlseat_iam"),
    serverCertificateRef: String(config.server_certificate_ref || ""),
    serverCertificateConfigured: config.server_certificate_configured === true || !!config.server_certificate_ref,
    serverPrivateKeyRef: String(config.server_private_key_ref || ""),
    serverPrivateKeyConfigured: config.server_private_key_configured === true || !!config.server_private_key_ref,
    applicationTrustListRef: String(config.application_trust_list_ref || ""),
    applicationTrustListConfigured: config.application_trust_list_configured === true || !!config.application_trust_list_ref,
    applicationIssuerListRef: String(config.application_issuer_list_ref || ""),
    applicationIssuerListConfigured: config.application_issuer_list_configured === true || !!config.application_issuer_list_ref,
    applicationCRLRef: String(config.application_crl_ref || ""),
    applicationCRLConfigured: config.application_crl_configured === true || !!config.application_crl_ref,
    x509UserTrustListRef: String(config.x509_user_trust_list_ref || ""),
    x509UserTrustListConfigured: config.x509_user_trust_list_configured === true || !!config.x509_user_trust_list_ref,
    x509UserIssuerListRef: String(config.x509_user_issuer_list_ref || ""),
    x509UserIssuerListConfigured: config.x509_user_issuer_list_configured === true || !!config.x509_user_issuer_list_ref,
    x509UserCRLRef: String(config.x509_user_crl_ref || ""),
    x509UserCRLConfigured: config.x509_user_crl_configured === true || !!config.x509_user_crl_ref,
    maxChannels: String(config.max_channels ?? 16),
    maxSessions: String(config.max_sessions ?? 16),
    maxSubscriptionsPerSession: String(config.max_subscriptions_per_session ?? 32),
    maxMonitoredItemsPerSubscription: String(config.max_monitored_items_per_subscription ?? 1024),
    maxNodesPerRead: String(config.max_nodes_per_read ?? 256),
    maxNodesPerWrite: String(config.max_nodes_per_write ?? 64),
    mappings: (Array.isArray(config.mappings) ? config.mappings : []).map(createOpcuaServerMapping),
  };
}

export function buildOpcuaServerPayload(form) {
  return {
    enabled: false,
    listen_address: joinListenAddress(form.listenHost, form.listenPort),
    namespace_uri: String(form.namespaceUri || "").trim(),
    source_cidrs: parseOpcuaSourceNetworks(form.sourceCidrsText),
    security_mode: "sign_and_encrypt",
    security_policy: "aes256_sha256_rsapss",
    anonymous_enabled: false,
    identity_mode: form.identityMode,
    identity_provider: String(form.identityProvider || "").trim(),
    server_certificate_ref: String(form.serverCertificateRef || "").trim(),
    server_private_key_ref: String(form.serverPrivateKeyRef || "").trim(),
    application_trust_list_ref: String(form.applicationTrustListRef || "").trim(),
    application_issuer_list_ref: String(form.applicationIssuerListRef || "").trim(),
    application_crl_ref: String(form.applicationCRLRef || "").trim(),
    x509_user_trust_list_ref: String(form.x509UserTrustListRef || "").trim(),
    x509_user_issuer_list_ref: String(form.x509UserIssuerListRef || "").trim(),
    x509_user_crl_ref: String(form.x509UserCRLRef || "").trim(),
    max_channels: integer(form.maxChannels),
    max_sessions: integer(form.maxSessions),
    max_subscriptions_per_session: integer(form.maxSubscriptionsPerSession),
    max_monitored_items_per_subscription: integer(form.maxMonitoredItemsPerSubscription),
    max_nodes_per_read: integer(form.maxNodesPerRead),
    max_nodes_per_write: integer(form.maxNodesPerWrite),
    mappings: (form.mappings || []).map((mapping) => ({
      node_id: String(mapping.nodeId || "").trim(),
      tag_path: String(mapping.tagPath || "").trim(),
      access: mapping.access === "read_write" ? "read_write" : "read",
    })),
  };
}

export function parseOpcuaSourceNetworks(value) {
  const seen = new Set();
  const result = [];
  String(value || "").split(/[\n,]+/).forEach((entry) => {
    const normalized = entry.trim();
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      result.push(normalized);
    }
  });
  return result;
}

const validReference = (value) => {
  const text = String(value || "");
  return text.length <= 512 && Array.from(text).every((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint >= 32 && codePoint !== 127;
  });
};

const plausibleSourceNetwork = (value) => {
  const text = String(value || "");
  if (!text || /\s/.test(text)) return false;
  const [address, rawPrefix, ...extra] = text.split("/");
  if (extra.length) return false;
  const ipv6 = address.includes(":");
  if (ipv6) {
    if (!/^[0-9a-fA-F:]+$/.test(address)) return false;
  } else {
    const octets = address.split(".");
    if (octets.length !== 4 || octets.some((octet) => !/^\d{1,3}$/.test(octet) || Number(octet) > 255)) return false;
  }
  if (rawPrefix == null) return true;
  const prefix = Number(rawPrefix);
  return Number.isInteger(prefix) && prefix >= 0 && prefix <= (ipv6 ? 128 : 32);
};

export function validateOpcuaServerForm(form) {
  const sourceNetworks = parseOpcuaSourceNetworks(form?.sourceCidrsText);
  const formErrors = {
    listenHost: validateListenHost(form?.listenHost),
    listenPort: requireInteger(form?.listenPort, "Listen port", 1, 65535),
    namespaceUri: /^urn:[^\s]{1,508}$/.test(String(form?.namespaceUri || "").trim())
      ? ""
      : "Namespace URI must be a URN no longer than 512 characters.",
    identityProvider: String(form?.identityProvider || "").trim()
      ? ""
      : "Identity provider is required.",
    sourceCidrs: sourceNetworks.length > 64
      ? "At most 64 source IP addresses or CIDRs are allowed."
      : sourceNetworks.find((value) => !plausibleSourceNetwork(value))
        ? "Each source network must be a canonical IPv4/IPv6 address or CIDR."
        : "",
    securityReferences: [
      form?.serverCertificateRef,
      form?.serverPrivateKeyRef,
      form?.applicationTrustListRef,
      form?.applicationIssuerListRef,
      form?.applicationCRLRef,
      form?.x509UserTrustListRef,
      form?.x509UserIssuerListRef,
      form?.x509UserCRLRef,
    ].every(validReference) ? "" : "Security reference names must be at most 512 printable characters.",
    maxChannels: requireInteger(form?.maxChannels, "Maximum channels", 1, 256),
    maxSessions: requireInteger(form?.maxSessions, "Maximum sessions", 1, 256),
    maxSubscriptionsPerSession: requireInteger(form?.maxSubscriptionsPerSession, "Subscriptions per session", 1, 1024),
    maxMonitoredItemsPerSubscription: requireInteger(form?.maxMonitoredItemsPerSubscription, "Monitored items per subscription", 1, 65536),
    maxNodesPerRead: requireInteger(form?.maxNodesPerRead, "Nodes per read", 1, 4096),
    maxNodesPerWrite: requireInteger(form?.maxNodesPerWrite, "Nodes per write", 1, 1024),
  };
  if (!formErrors.maxChannels && !formErrors.maxSessions && integer(form.maxSessions) > integer(form.maxChannels)) {
    formErrors.maxSessions = "Maximum sessions cannot exceed maximum channels.";
  }
  const mappings = Array.isArray(form?.mappings) ? form.mappings : [];
  const mappingErrors = mappings.map((mapping) => {
    let error = "";
    const nodeId = String(mapping?.nodeId || "").trim();
    if (!nodeId || nodeId.length > 256 || hasForbiddenNodeIdCharacter(nodeId)) {
      error = appendError(error, "Node identifier must be 1–256 printable characters without ';' or '='.");
    }
    if (!String(mapping?.tagPath || "").trim()) {
      error = appendError(error, "Tag path is required.");
    }
    if (!["read", "read_write"].includes(mapping?.access)) {
      error = appendError(error, "Select read-only or read/write access.");
    }
    return error;
  });
  const nodeIds = new Map();
  const tagPaths = new Map();
  mappings.forEach((mapping, index) => {
    const nodeId = String(mapping?.nodeId || "").trim();
    const tagPath = String(mapping?.tagPath || "").trim();
    if (nodeIds.has(nodeId)) {
      const prior = nodeIds.get(nodeId);
      mappingErrors[prior] = appendError(mappingErrors[prior], "Node identifier is duplicated.");
      mappingErrors[index] = appendError(mappingErrors[index], "Node identifier is duplicated.");
    } else if (nodeId) nodeIds.set(nodeId, index);
    if (tagPaths.has(tagPath)) {
      const prior = tagPaths.get(tagPath);
      mappingErrors[prior] = appendError(mappingErrors[prior], "A tag can be exposed only once.");
      mappingErrors[index] = appendError(mappingErrors[index], "A tag can be exposed only once.");
    } else if (tagPath) tagPaths.set(tagPath, index);
  });
  const firstFormError = Object.values(formErrors).find(Boolean) || "";
  const firstMappingError = mappingErrors.find(Boolean) || "";
  return {
    valid: !firstFormError && !firstMappingError,
    message: firstFormError || firstMappingError,
    formErrors,
    mappingErrors,
  };
}

export function normalizeOpcuaServerStatus(status = {}) {
  const projection = status.read_projection && typeof status.read_projection === "object"
    ? status.read_projection
    : {};
  return {
    profile: String(status.profile || ""),
    available: status.available === true,
    executable: status.executable === true,
    enabled: status.enabled === true,
    running: status.running === true,
    mode: String(status.mode || "configuration_only"),
    blockerCode: String(status.blocker_code || ""),
    reason: String(status.reason || ""),
    listenAddress: String(status.listen_address || ""),
    namespaceUri: String(status.namespace_uri || ""),
    mappingCount: Number(status.mapping_count || 0),
    desiredRevision: Number(status.desired_revision || 0),
    appliedRevision: Number(status.applied_revision || 0),
    desiredEqualsApplied: status.desired_equals_applied === true,
    runtimeMatchesApplied: status.runtime_matches_applied === true,
    reconciled: status.reconciled === true,
    listenerClosedApplied: status.listener_closed_applied === true,
    writeStateContract: String(status.write_state_contract || ""),
    warnings: Array.isArray(status.warnings) ? status.warnings.map(String) : [],
    dependencies: Array.isArray(status.dependencies) ? status.dependencies : [],
    readProjection: {
      available: projection.available === true,
      profile: String(projection.profile || ""),
      scalarTypes: Array.isArray(projection.scalar_types) ? projection.scalar_types.map(String) : [],
      maxRequestBytes: Number(projection.max_request_bytes || 0),
      maxResponseBytes: Number(projection.max_response_bytes || 0),
      maxScalarBytes: Number(projection.max_scalar_bytes || 0),
      writesEnabled: projection.writes_enabled === true,
      networkListenerOwned: projection.network_listener_owned === true,
    },
  };
}

const canonicalUUID = (value) => (
  typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value)
);
const canonicalSHA256 = (value) => typeof value === "string" && /^[0-9a-f]{64}$/.test(value);
const nonNegativeInteger = (value) => Number.isSafeInteger(value) && value >= 0;

export function assertOpcuaServerEditConfig(payload) {
  const revision = Number(payload?.config?.revision || 0);
  const digest = String(payload?.config?.digest || "");
  if (
    payload?.schema !== "controlseat.opcua-server-config-edit" || payload?.version !== 1 ||
    !payload.config || typeof payload.config !== "object" ||
    !nonNegativeInteger(revision) ||
    (revision > 0 && !canonicalSHA256(digest)) || (revision === 0 && digest !== "")
  ) {
    throw new Error("The gateway did not return a valid editable OPC UA server configuration.");
  }
  return payload;
}

export function assertOpcuaServerPreview(payload, expectedBaseRevision) {
  const proposal = payload?.proposal;
  const semanticDiff = proposal?.semantic_diff;
  if (
    payload?.schema !== "controlseat.opcua-server-config-preview" || payload?.version !== 1 ||
    !canonicalUUID(proposal?.confirmation_token) || !canonicalSHA256(proposal?.proposal_sha256) ||
    !canonicalSHA256(proposal?.candidate_digest) ||
    proposal?.base_revision !== expectedBaseRevision || !nonNegativeInteger(proposal?.base_revision) ||
    !Array.isArray(semanticDiff) || semanticDiff.length > 512 ||
    semanticDiff.some((change) => !change || typeof change.field !== "string" || !change.field.trim() ||
      typeof change.before !== "string" || typeof change.after !== "string" ||
      /\btag[_ -]?id\b|tagId/i.test(`${change.field} ${change.before} ${change.after}`)) ||
    typeof proposal?.expires_at !== "string" || !Number.isFinite(Date.parse(proposal.expires_at)) ||
    proposal?.confirmation_required !== true ||
    !Array.isArray(proposal?.warnings) || proposal.warnings.some((warning) => typeof warning !== "string")
  ) {
    throw new Error("The gateway did not return a valid reviewed OPC UA server configuration preview.");
  }
  return payload;
}

export function assertOpcuaServerConfirmation(payload) {
  const config = payload?.config;
  if (
    payload?.schema !== "controlseat.opcua-server-config-confirmation" || payload?.version !== 1 ||
    !config || !nonNegativeInteger(config.revision) || config.revision < 1 ||
    Object.prototype.hasOwnProperty.call(config, "digest") || config.enabled !== false ||
    !payload.status || payload.status.desired_revision !== config.revision ||
    Object.prototype.hasOwnProperty.call(payload.status, "desired_digest") ||
    Object.prototype.hasOwnProperty.call(payload.status, "applied_digest") ||
    Number(payload.status.applied_revision || 0) !== 0 || payload.status.desired_equals_applied !== false ||
    payload.status.runtime_matches_applied !== false || payload.status.reconciled !== false ||
    payload.status.listener_closed_applied !== false || payload.status.available !== false ||
    payload.status.executable !== false || payload.status.enabled !== false || payload.status.running !== false ||
    payload.status.mode !== "configuration_only"
  ) {
    throw new Error("The gateway did not return a valid desired-only OPC UA server confirmation.");
  }
  return payload;
}

export function opcuaServerPreviewExpired(proposal, now = Date.now()) {
  const expiresAt = Date.parse(proposal?.expires_at);
  return !Number.isFinite(expiresAt) || expiresAt <= now;
}
